import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { Container, Overline } from '@/design-system/primitives'
import { RubricPage } from '@/features/event/components/RubricPage'
import { usePublicMenu } from '@/features/food/api'
import { usePublicDrinks } from '@/features/tables/api'
import { QuantityStepper } from '@/features/tickets/components'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { formatMoney, minorUnits } from '@/lib/money'

import {
  useCreateOnsiteOrder,
  useLookupTable,
  useOnsiteConfig,
  useOnsiteOrderStatus,
  useOnsiteQuote,
  type OnsiteOrder,
  type OnsiteSelection,
  type OnsiteTable,
} from '../api'

/**
 * Commander depuis sa table, le soir J.
 *
 * Trois temps, dans l'ordre : **confirmer sa table** (le QR la pré-remplit,
 * l'invité la reconnaît à son nom — rien ne s'affiche avant), composer son
 * panier, choisir comment il paiera. Le total affiché est celui que le serveur
 * calcule à chaque changement de panier ; c'est le même qui sera facturé.
 *
 * La commande part ensuite chez le serveur de la table, qui vient encaisser.
 * Elle n'arrive en cuisine ou au bar qu'une fois réglée.
 */
export function OnsitePage() {
  const { t } = useTranslation()

  return (
    <RubricPage title={t('onsite.title')} intro={t('onsite.intro')}>
      {(event) => <OnsiteBody slug={event.slug} />}
    </RubricPage>
  )
}

function OnsiteBody({ slug }: { slug: string }) {
  const { t, i18n } = useTranslation()
  const [params] = useSearchParams()

  const configQuery = useOnsiteConfig(slug)
  const menuQuery = usePublicMenu(slug)
  const drinksQuery = usePublicDrinks(slug)
  const createOrder = useCreateOnsiteOrder(slug)
  const lookup = useLookupTable(slug)

  // Le QR posé sur la table pré-remplit son numéro : `?table=A3`. Il reste à
  // le CONFIRMER : un QR recollé sur la mauvaise table enverrait le plateau
  // ailleurs.
  const fromQr = params.get('table') ?? ''
  const [tableNumber, setTableNumber] = useState(fromQr)
  const [table, setTable] = useState<OnsiteTable | null>(null)
  const [suggested, setSuggested] = useState<OnsiteTable | null>(null)
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [method, setMethod] = useState<'paypal' | 'cash'>('paypal')
  const [cashGiven, setCashGiven] = useState('')
  const [note, setNote] = useState('')
  // « Je n'ai pas de table » : le prénom remplace la table. `asking` ouvre le
  // petit formulaire ; `guest` est le prénom confirmé.
  const [asking, setAsking] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [spot, setSpot] = useState('')
  const [guest, setGuest] = useState<{ name: string; spot: string } | null>(null)
  // La dernière commande passée sur ce téléphone, gardée pour la suivre : on
  // revient de PayPal, on recharge la page en attendant au comptoir — le
  // numéro et son statut doivent toujours être là.
  const [placed, setPlacedState] = useState<OnsiteOrder | null>(() => readPlaced(slug))
  const setPlaced = (order: OnsiteOrder | null) => {
    writePlaced(slug, order)
    setPlacedState(order)
  }

  const config = configQuery.data
  const currency = config?.currency ?? 'EUR'

  // Le numéro lu sur le QR est vérifié d'office ; l'invité n'a plus qu'à dire
  // « oui, c'est ma table ».
  const lookupMutate = lookup.mutate
  useEffect(() => {
    if (!fromQr || !slug) return
    lookupMutate(fromQr, { onSuccess: setSuggested })
  }, [fromQr, slug, lookupMutate])

  /**
   * Le catalogue commandable, rangé par rubriques.
   *
   * Une liste de quarante lignes d'affilée ne se lit pas depuis une table,
   * dans le bruit. On regroupe donc : la cuisine d'abord, puis le bar, séparé
   * entre ce qui contient de l'alcool et ce qui n'en contient pas — c'est le
   * premier tri que fait quelqu'un qui commande.
   *
   * Le rangement suit les **catégories saisies en back-office** : le code ne
   * devine jamais si une bouteille est alcoolisée. Les boissons sans
   * catégorie retombent sur `is_extra`, le drapeau qui distingue déjà les
   * softs des bouteilles.
   */
  const groups = useMemo(() => {
    interface Orderable {
      key: string
      kind: OnsiteSelection['kind']
      ref_id: string
      label: string
      price_minor: number
    }

    const food: Orderable[] = (menuQuery.data?.items ?? [])
      .filter((item) => item.availability !== 'sold_out')
      .map((item) => ({
        key: `food_item:${item.id}`,
        kind: 'food_item' as const,
        ref_id: item.id,
        label: item.name,
        price_minor: item.price_minor,
      }))

    const alcoholic: Orderable[] = []
    const soft: Orderable[] = []
    for (const drink of drinksQuery.data?.drinks ?? []) {
      if (drink.availability === 'sold_out') continue
      const entry: Orderable = {
        key: `drink:${drink.id}`,
        kind: 'drink' as const,
        ref_id: drink.id,
        label: [drink.name, drink.size_label].filter(Boolean).join(' · '),
        price_minor: drink.price_minor,
      }
      // `is_extra` distingue déjà les softs des bouteilles dans le calcul des
      // franchises de table : on s'appuie dessus plutôt que d'inventer un
      // second drapeau que l'équipe devrait tenir à jour deux fois.
      ;(drink.is_extra ? soft : alcoholic).push(entry)
    }

    return [
      { id: 'food', title: t('onsite.groupFood'), entries: food },
      { id: 'alcohol', title: t('onsite.groupAlcohol'), entries: alcoholic },
      { id: 'soft', title: t('onsite.groupSoft'), entries: soft },
    ].filter((group) => group.entries.length > 0)
  }, [menuQuery.data, drinksQuery.data, t])

  const catalogue = useMemo(() => groups.flatMap((group) => group.entries), [groups])

  const selections: OnsiteSelection[] = catalogue
    .filter((entry) => (quantities[entry.key] ?? 0) > 0)
    .map((entry) => ({
      kind: entry.kind,
      ref_id: entry.ref_id,
      quantity: quantities[entry.key] ?? 0,
    }))

  // Le total vient du serveur, panier après panier : c'est le même chiffre
  // qui sera facturé. Rien n'est additionné ici.
  const quote = useOnsiteQuote(slug, selections)
  const total = selections.length === 0 ? 0 : (quote.data?.total_minor ?? null)
  const threshold = config?.table_service_min_minor ?? 0
  const willBeServed = (total ?? 0) >= threshold

  // Ce que l'invité a sur lui est facultatif : le serveur saisira ce qu'il
  // reçoit vraiment. Indiqué, il doit couvrir la commande.
  const digits = minorUnits(currency)
  const cashValue = Number(cashGiven.replace(',', '.'))
  const cashMinor =
    cashGiven.trim() && Number.isFinite(cashValue) ? Math.round(cashValue * 10 ** digits) : null
  const cashIsValid = method !== 'cash' || cashMinor === null || cashMinor >= (total ?? 0)

  function money(minor: number) {
    return formatMoney({ amount_minor: minor, currency }, i18n.language)
  }

  async function submit() {
    if (!table && !guest) return
    setPlaced(
      await createOrder.mutateAsync({
        ...(table
          ? { table_number: table.table_number }
          : {
              customer_label: guest?.name ?? '',
              ...(guest?.spot ? { spot_note: guest.spot } : {}),
            }),
        items: selections,
        payment_method: method,
        ...(method === 'cash' && cashMinor !== null ? { cash_tendered_minor: cashMinor } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    )
  }

  if (configQuery.isPending) return null

  // Une commande passée se suit même si la commande vient d'être fermée : on
  // attend toujours son plat.
  if (placed) return <Confirmation order={placed} onNewOrder={() => setPlaced(null)} />

  if (!config?.enabled) {
    return (
      <Container className="py-12">
        <p className="text-body-lg text-content-secondary">{t('onsite.closed')}</p>
      </Container>
    )
  }

  // Le réglage a pu se fermer pendant que l'invité composait : l'option
  // disparaît, et il revient au choix de la table.
  const untabled = guest !== null && config.allow_no_table

  const failure = createOrder.error ?? quote.error
  const message =
    failure instanceof ApiError
      ? (t(`onsite.error.${failure.code}`, { defaultValue: failure.message }) as string)
      : null

  // ── 1. La table d'abord : rien ne s'affiche avant qu'elle soit confirmée ──
  // (ou, sans table, avant que le prénom soit donné)
  if (!table && !untabled) {
    const lookupError =
      lookup.error instanceof ApiError
        ? (t(`onsite.error.${lookup.error.code}`, { defaultValue: lookup.error.message }) as string)
        : null
    if (asking && config.allow_no_table) {
      return (
        <Container className="max-w-md py-12">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              const name = firstName.trim()
              if (name) setGuest({ name, spot: spot.trim() })
            }}
          >
            <Overline>{t('onsite.noTableTitle')}</Overline>
            <p className="mt-2 text-sm text-content-secondary">{t('onsite.noTableHint')}</p>
            <label className="mt-4 block text-sm text-content-secondary" htmlFor="guest-name">
              {t('onsite.firstName')}
            </label>
            <input
              id="guest-name"
              value={firstName}
              maxLength={60}
              autoComplete="given-name"
              autoCapitalize="words"
              onChange={(event) => setFirstName(event.target.value)}
              className="mt-2 h-14 w-full rounded-lg border border-line bg-bg-elevated px-4 text-heading-sm text-content-primary"
            />
            <label className="mt-4 block text-sm text-content-secondary" htmlFor="guest-spot">
              {t('onsite.spotNote')}
            </label>
            <input
              id="guest-spot"
              value={spot}
              maxLength={60}
              autoComplete="off"
              placeholder={t('onsite.spotPlaceholder')}
              onChange={(event) => setSpot(event.target.value)}
              className="mt-2 h-12 w-full rounded-lg border border-line bg-bg-elevated px-4 text-content-primary"
            />
            <Button type="submit" size="lg" className="mt-6 w-full" disabled={!firstName.trim()}>
              {t('onsite.continue')}
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="mt-2 w-full"
              onClick={() => setAsking(false)}
            >
              {t('onsite.haveTable')}
            </Button>
          </form>
        </Container>
      )
    }
    return (
      <Container className="max-w-md py-12">
        {suggested ? (
          <div className="rounded-lg border border-champagne-400/40 bg-bg-elevated p-6">
            <Overline>{t('onsite.confirmTable')}</Overline>
            <p className="mt-3 font-display text-display-sm text-content-primary">
              {suggested.table_number}
            </p>
            {suggested.name ? (
              <p className="text-heading-sm text-champagne-300">{suggested.name}</p>
            ) : null}
            {suggested.waiter_first_name ? (
              <p className="mt-2 text-sm text-content-secondary">
                {t('onsite.yourWaiter', { name: suggested.waiter_first_name })}
              </p>
            ) : null}
            {/* L'un sous l'autre sur téléphone : côte à côte, le bouton
                principal se tassait jusqu'à couper son propre libellé. */}
            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <Button size="lg" className="w-full sm:flex-1" onClick={() => setTable(suggested)}>
                {t('onsite.thisIsMyTable')}
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className="w-full sm:w-auto"
                onClick={() => setSuggested(null)}
              >
                {t('onsite.otherTable')}
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault()
              if (tableNumber.trim()) {
                lookup.mutate(tableNumber.trim(), { onSuccess: setSuggested })
              }
            }}
          >
            <Overline>{t('onsite.stepTable')}</Overline>
            <p className="mt-2 text-sm text-content-secondary">{t('onsite.tableHint')}</p>
            <input
              value={tableNumber}
              onChange={(event) => setTableNumber(event.target.value)}
              placeholder={t('onsite.tablePlaceholder')}
              aria-label={t('onsite.stepTable')}
              autoCapitalize="characters"
              className="mt-4 h-14 w-full rounded-lg border border-line bg-bg-elevated px-4 text-heading-sm text-content-primary"
            />
            {lookupError ? <p className="mt-3 text-sm text-ember">{lookupError}</p> : null}
            <Button
              type="submit"
              size="lg"
              className="mt-4 w-full"
              disabled={!tableNumber.trim() || lookup.isPending}
            >
              {lookup.isPending ? t('state.loading') : t('onsite.continue')}
            </Button>
          </form>
        )}
        {/* Debout au bar, près de la piste : on commande quand même, avec
            son prénom, et on retire au comptoir. */}
        {config.allow_no_table ? (
          <div className="mt-8 border-t border-line-subtle pt-6">
            <Button variant="outline" size="lg" className="w-full" onClick={() => setAsking(true)}>
              {t('onsite.noTable')}
            </Button>
          </div>
        ) : null}
      </Container>
    )
  }

  return (
    <Container className="py-12">
      {/* Colonnes en minmax(0, …) : un nom de plat long, tronqué mais
          insécable, élargissait sinon la grille au-delà de l'écran. */}
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:items-start">
        <div className="space-y-8">
          <section className="flex flex-wrap items-baseline justify-between gap-3 rounded-lg border border-line-subtle bg-bg-elevated px-4 py-3">
            {table ? (
              <div className="min-w-0">
                <Overline>{t('onsite.stepTable')}</Overline>
                <p className="mt-1 font-display text-heading-md text-content-primary">
                  {[table.table_number, table.name].filter(Boolean).join(' · ')}
                </p>
              </div>
            ) : (
              <div className="min-w-0">
                <Overline>{t('onsite.noTableTitle')}</Overline>
                <p className="mt-1 break-words font-display text-heading-md text-content-primary">
                  {guest?.name}
                  {guest?.spot ? (
                    <span className="text-heading-sm text-content-secondary"> ({guest.spot})</span>
                  ) : null}
                </p>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setTable(null)
                setSuggested(null)
                setGuest(null)
                setAsking(false)
              }}
            >
              {table ? t('onsite.otherTable') : t('onsite.haveTable')}
            </Button>
          </section>

          <section>
            <Overline>{t('onsite.stepItems')}</Overline>
            {catalogue.length === 0 ? (
              <p className="mt-3 text-content-muted">{t('onsite.emptyCatalogue')}</p>
            ) : (
              groups.map((group) => (
                <div key={group.id} className="mt-6">
                  <p className="overline text-champagne-400">{group.title}</p>
                  <ul className="mt-3 divide-y divide-line-subtle">
                    {group.entries.map((entry) => (
                      <li
                        key={entry.key}
                        className="flex items-center justify-between gap-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-content-primary">{entry.label}</p>
                          <p className="text-caption text-content-muted">
                            {money(entry.price_minor)}
                          </p>
                        </div>
                        <QuantityStepper
                          value={quantities[entry.key] ?? 0}
                          onChange={(value) =>
                            setQuantities((current) => ({ ...current, [entry.key]: value }))
                          }
                          max={50}
                          label={entry.label}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </section>

          <section>
            <Overline>{t('onsite.stepNote')}</Overline>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={2}
              maxLength={500}
              placeholder={t('onsite.notePlaceholder')}
              className="mt-3 w-full rounded-lg border border-line bg-bg-elevated px-4 py-3 text-content-primary"
            />
          </section>
        </div>

        <aside className="rounded-lg border border-line-subtle bg-bg-elevated p-6 lg:sticky lg:top-24">
          {/* Le panier, ligne à ligne, tel que le serveur l'a chiffré. */}
          {quote.data && selections.length > 0 ? (
            <ul className="mb-4 space-y-1 border-b border-line-subtle pb-3 text-sm">
              {quote.data.lines.map((line) => (
                <li key={`${line.kind}:${line.ref_id}`} className="flex justify-between gap-3">
                  <span className="min-w-0 truncate text-content-secondary">
                    {line.quantity} × {line.label}
                  </span>
                  <span className="shrink-0 tabular-nums text-content-primary">
                    {money(line.line_total_minor)}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <p className="flex items-baseline justify-between">
            <span className="text-content-secondary">{t('onsite.total')}</span>
            <span className="font-display text-heading-md text-content-primary">
              {total === null ? '…' : money(total)}
            </span>
          </p>
          <p className="mt-1 text-caption text-content-muted">{t('onsite.serverTotalHint')}</p>

          <p className="mt-4 rounded-md bg-bg-base px-4 py-3 text-sm text-content-secondary">
            {untabled
              ? t('onsite.pickupNoTable')
              : willBeServed
                ? t('onsite.willServe')
                : t('onsite.willPickup', { amount: money(threshold) })}
          </p>

          <fieldset className="mt-6">
            <legend className="overline text-content-muted">{t('onsite.stepPayment')}</legend>
            <div className="mt-3 flex gap-2">
              <Button
                variant={method === 'paypal' ? 'ember' : 'outline'}
                size="sm"
                onClick={() => setMethod('paypal')}
              >
                PayPal
              </Button>
              {config.cash_enabled ? (
                <Button
                  variant={method === 'cash' ? 'ember' : 'outline'}
                  size="sm"
                  onClick={() => setMethod('cash')}
                >
                  {t('onsite.cash')}
                </Button>
              ) : null}
            </div>
          </fieldset>

          {method === 'cash' ? (
            <div className="mt-4">
              <label className="text-sm text-content-secondary" htmlFor="cash-given">
                {t('onsite.cashGiven')}
              </label>
              <input
                id="cash-given"
                inputMode="decimal"
                value={cashGiven}
                onChange={(event) => setCashGiven(event.target.value)}
                className="mt-2 h-12 w-full rounded-lg border border-line bg-bg-base px-4 text-content-primary"
              />
              <p className="mt-2 text-caption text-content-muted">
                {untabled ? t('onsite.cashHintCounter') : t('onsite.cashHint')}
              </p>
            </div>
          ) : null}

          {message ? <p className="mt-4 text-sm text-ember">{message}</p> : null}

          <Button
            size="lg"
            className="mt-6 w-full"
            disabled={
              selections.length === 0 ||
              total === null ||
              quote.isError ||
              !cashIsValid ||
              createOrder.isPending
            }
            onClick={() => void submit()}
          >
            {createOrder.isPending ? t('state.loading') : t('onsite.submit')}
          </Button>
        </aside>
      </div>
    </Container>
  )
}

/**
 * Ce que le client voit une fois la commande passée.
 *
 * Tout ce qui est affiché ici vient du **serveur** : le total facturé, le mode
 * de service retenu, et la monnaie à rendre. Rien n'est recalculé.
 */
function Confirmation({ order: placed, onNewOrder }: { order: OnsiteOrder; onNewOrder: () => void }) {
  const { t, i18n } = useTranslation()
  // Le statut se relit seul : « en préparation », puis « prête ». Le lien
  // PayPal, lui, ne vient que de la création — le suivi ne le renvoie pas.
  const status = useOnsiteOrderStatus(placed.order_id, placed.access_token)
  const order: OnsiteOrder = status.data
    ? { ...status.data, approval_url: placed.approval_url }
    : placed
  const untabled = !order.table_number
  const pickup = untabled || order.service_mode === 'counter_pickup'
  const state = order.prep_state ?? 'unpaid'

  // Le panier se validait en bas de page : sans cela, le numéro et le statut
  // apparaissaient hors de l'écran, et l'invité croyait que rien n'était parti.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  function money(minor: number) {
    return formatMoney({ amount_minor: minor, currency: order.currency }, i18n.language)
  }

  // PayPal : le paiement se termine chez eux. Le lien est affiché plutôt que
  // suivi d'office — une redirection automatique surprend, et certains
  // navigateurs la bloquent.
  return (
    <Container className="py-12">
      <Overline>{t('onsite.placedOverline')}</Overline>
      {untabled ? (
        <>
          {/* Le numéro d'un seul tenant : coupé à son tiret, « MN26- » et
              « 000123 » se lisaient comme deux choses à annoncer. */}
          <h2 className="mt-3 font-display text-heading-lg text-content-primary">
            {t('onsite.pickupTitle')}{' '}
            <span className="whitespace-nowrap">{order.order_number}</span>
          </h2>
          <p className="mt-2 max-w-xl text-content-secondary">{t('onsite.pickupBody')}</p>
        </>
      ) : (
        <h2 className="mt-3 font-display text-heading-lg text-content-primary">
          {order.order_number}
        </h2>
      )}

      {/* Le statut, en grand : c'est ce que l'invité regarde en attendant. */}
      <p
        role="status"
        aria-live="polite"
        className={cn(
          'mt-6 max-w-xl rounded-lg border px-5 py-4 font-display text-heading-sm',
          state === 'ready'
            ? 'border-state-success bg-state-success/10 text-state-success'
            : state === 'cancelled'
              ? 'border-line text-content-muted'
              : 'border-champagne-400/40 bg-bg-elevated text-content-primary',
        )}
      >
        {state === 'ready'
          ? pickup
            ? t('onsite.prep.readyPickup')
            : t('onsite.prep.readyServed')
          : t(`onsite.prep.${state}`)}
      </p>

      <dl className="mt-8 space-y-3">
        {untabled ? (
          <Row
            label={t('onsite.name')}
            value={[order.customer_label, order.spot_note ? `(${order.spot_note})` : null]
              .filter(Boolean)
              .join(' ')}
          />
        ) : (
          <Row
            label={t('onsite.table')}
            value={[order.table_number, order.table_name].filter(Boolean).join(' · ')}
          />
        )}
        <Row label={t('onsite.total')} value={money(order.total_minor)} />
        {/* Qui vient encaisser : le serveur de la table. */}
        {order.waiter_first_name ? (
          <Row label={t('onsite.waiter')} value={order.waiter_first_name} />
        ) : null}
        <Row
          label={t('onsite.service')}
          value={
            untabled
              ? t('onsite.pickupAtCounter')
              : order.service_mode === 'table_service'
                ? t('onsite.willServe')
                : t('onsite.willPickup', { amount: money(order.table_service_min_minor) })
          }
        />
        {order.change_due_minor !== null ? (
          <Row label={t('onsite.change')} value={money(order.change_due_minor)} />
        ) : null}
      </dl>

      {/* PayPal sans API : un lien PayPal.me ne dit pas qui paie. La
          référence, recopiée dans le message du paiement, est ce qui permet à
          l'équipe de reconnaître ce virement et de lancer la commande. */}
      {order.payment_reference ? (
        <div className="mt-8 max-w-xl rounded-lg border border-ember-500/40 bg-ember-500/10 px-6 py-4">
          <p className="overline text-content-muted">{t('payment.referenceLabel')}</p>
          <p className="mt-1 select-all font-display text-heading-lg tracking-wide text-ember-300">
            {order.payment_reference}
          </p>
          <p className="mt-1 text-xs text-content-muted">{t('onsite.referenceHint')}</p>
        </div>
      ) : null}

      {state !== 'unpaid' ? null : order.approval_url ? (
        <>
          <p className="mt-8 max-w-xl text-content-secondary">{t('onsite.paypalPending')}</p>
          <a
            href={order.approval_url}
            className="mt-4 inline-flex h-12 items-center rounded-full bg-ember px-6 text-sm font-medium text-white"
          >
            {t('onsite.paypalContinue')}
          </a>
        </>
      ) : (
        // Sans lien de redirection, deux cas très différents : les espèces
        // (quelqu'un passe encaisser — ou, sans table, on règle au comptoir)
        // ou un règlement en ligne dont le fournisseur ne redirige pas.
        // Annoncer « un serveur passe » dans le second cas ferait attendre
        // pour rien.
        <p className="mt-8 max-w-xl text-content-secondary">
          {order.payment_method !== 'cash'
            ? t('onsite.onlinePending')
            : untabled
              ? t('onsite.cashPendingCounter')
              : t('onsite.cashPending')}
        </p>
      )}

      <Button variant="outline" size="lg" className="mt-10 w-full sm:w-auto" onClick={onNewOrder}>
        {t('onsite.newOrder')}
      </Button>
    </Container>
  )
}

// ── La dernière commande, gardée sur ce téléphone ──────────────────

/** Au-delà, la commande est d'une autre soirée : on repart d'une page neuve. */
const KEEP_MS = 12 * 60 * 60 * 1000

function storageKey(slug: string) {
  return `onsite-order:${slug}`
}

/**
 * La commande en cours de suivi, si ce téléphone en a passé une.
 *
 * Le stockage du navigateur peut manquer (navigation privée, données
 * effacées) : on retombe alors sur la page de commande, rien de plus.
 */
function readPlaced(slug: string): OnsiteOrder | null {
  try {
    const raw = window.localStorage.getItem(storageKey(slug))
    if (!raw) return null
    const saved = JSON.parse(raw) as { at: number; order: OnsiteOrder }
    if (!saved.order?.order_id || Date.now() - saved.at > KEEP_MS) return null
    return saved.order
  } catch {
    return null
  }
}

function writePlaced(slug: string, order: OnsiteOrder | null) {
  try {
    if (order) {
      window.localStorage.setItem(storageKey(slug), JSON.stringify({ at: Date.now(), order }))
    } else {
      window.localStorage.removeItem(storageKey(slug))
    }
  } catch {
    // Sans stockage, le suivi ne survit pas au rechargement — la commande,
    // elle, est bien partie.
  }
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-6 border-b border-line-subtle pb-3">
      <dt className="text-content-secondary">{label}</dt>
      <dd className="text-content-primary">{value}</dd>
    </div>
  )
}
